import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronDown, X } from 'lucide-react';
import UserService from '../../../services/user.service';
import { startPersonalCheckout } from '../../../utils/personalCheckout';
import {
    modalBody,
    modalCloseBtn,
    modalField,
    modalLabel,
    modalNotice,
    modalPrimaryBtn,
    modalSecondaryBtn,
    modalSubtitle,
    modalTitle,
} from '../../../components/ui/modalDesign';
import { useRegistration } from '../../../components/layouts/app/RegistrationContext';

type Slot = {
    watDay: number;
    watHour: number;
    dayText: string;
    timeText: string;
    localLabel: string;
};

type Selection = { watDay: number; watHour: number } | null;

function savedSelections(enrollment: any): Selection[] {
    let raw = enrollment?.slots;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            raw = [];
        }
    }
    if (raw && !Array.isArray(raw) && typeof raw === 'object') raw = Object.values(raw);
    const saved = Array.isArray(raw) ? raw : [];
    return saved
        .map((slot: any) => ({
            watDay: Number(slot?.watDay),
            watHour: Number(slot?.watHour),
        }))
        .filter((slot: Selection) => slot && Number.isInteger(slot.watDay) && Number.isInteger(slot.watHour));
}

export type PersonalClassModalHandle = {
    requestClose: () => void;
};

const PersonalClassModal = forwardRef<PersonalClassModalHandle, {
    onClose: () => void;
    onPaid?: () => void;
    initialEnrollment?: any;
}>(function PersonalClassModal({
    onClose,
    onPaid,
    initialEnrollment,
}, ref) {
    const userService = useMemo(() => new UserService(), []);
    const { openAddChildOnly } = useRegistration();
    const [packages, setPackages] = useState<any[]>([]);
    const [slots, setSlots] = useState<Slot[]>([]);
    const [children, setChildren] = useState<any[]>([]);
    const [packageId, setPackageId] = useState('');
    const [childId, setChildId] = useState('');
    const [grade, setGrade] = useState('');
    const [subject, setSubject] = useState('');
    const [subjectQuery, setSubjectQuery] = useState('');
    const [subjectOpen, setSubjectOpen] = useState(false);
    const subjectMenuRef = useRef<HTMLDivElement>(null);
    const [classesPerWeek, setClassesPerWeek] = useState(2);
    const weeks = 4;
    const [selections, setSelections] = useState<Selection[]>([null, null]);
    const [quote, setQuote] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<null | 'checkout' | 'save'>(null);
    const [reviewing, setReviewing] = useState(false);
    const persisted = useRef(false);
    const closeLock = useRef(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [pkgRes, childRes] = await Promise.all([
                    userService.getPersonalClassPackages(),
                    userService.getAllChildren(),
                ]);
                if (cancelled) return;
                const list = pkgRes?.data?.packages || [];
                setPackages(list);
                const loadedSlots = pkgRes?.data?.slots;
                setSlots(Array.isArray(loadedSlots) ? loadedSlots : []);
                setChildren(Array.isArray(childRes?.data) ? childRes.data : []);
            } catch (err: any) {
                toast.error(err?.message || 'Unable to load private lessons');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [userService]);

    useEffect(() => {
        const refreshChildren = async () => {
            try {
                const childRes = await userService.getAllChildren();
                const list = Array.isArray(childRes?.data) ? childRes.data : [];
                setChildren(list);
                setChildId((current) => {
                    if (current && list.some((item: any) => String(item.id) === String(current))) return current;
                    return list.length === 1 ? String(list[0].id) : '';
                });
            } catch {
                /* ignore */
            }
        };
        window.addEventListener('ryd-reg-flow-ended', refreshChildren);
        return () => window.removeEventListener('ryd-reg-flow-ended', refreshChildren);
    }, [userService]);

    useEffect(() => {
        if (!subjectOpen) return undefined;
        const close = (event: MouseEvent) => {
            if (!subjectMenuRef.current?.contains(event.target as Node)) setSubjectOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [subjectOpen]);

    const offeredSubjects = packages.flatMap((pkg) =>
        (Array.isArray(pkg.subjects) ? pkg.subjects : []).map((name: string) => ({ name, packageId: String(pkg.id) }))
    );
    const filteredSubjects = offeredSubjects.filter((item) =>
        item.name.toLowerCase().includes(subjectQuery.trim().toLowerCase())
    );

    const chooseSubject = (next: { name: string; packageId: string }) => {
        setSubject(next.name);
        setPackageId(next.packageId);
        setSubjectQuery('');
        setSubjectOpen(false);
    };

    useEffect(() => {
        if (!initialEnrollment) return;
        const childValue = initialEnrollment.child?.id || initialEnrollment.childId;
        if (childValue) setChildId(String(childValue));
        if (initialEnrollment.grade) setGrade(initialEnrollment.grade);
        const subjectValue = initialEnrollment.subject || initialEnrollment.package?.title || '';
        if (subjectValue) setSubject(subjectValue);
        const savedPackageId = initialEnrollment.package?.id || initialEnrollment.packageId;
        if (savedPackageId) setPackageId(String(savedPackageId));

        if (slots.length === 0) return;
        const allowSlots = initialEnrollment.prefillSlots !== false;
        const saved = allowSlots ? savedSelections(initialEnrollment) : [];
        const count = Math.min(
            4,
            Math.max(1, Number(initialEnrollment.classesPerWeek) || (allowSlots ? saved.length : 0) || 2)
        );
        const next = saved.slice(0, count);
        while (next.length < count) next.push(null);
        setClassesPerWeek(count);
        setSelections(next);
    }, [initialEnrollment, slots]);

    // When subject was prefilled (e.g. signup Mathematics) match it to an offered package subject.
    useEffect(() => {
        if (!subject || packageId || packages.length === 0) return;
        for (const pkg of packages) {
            const names = Array.isArray(pkg.subjects) ? pkg.subjects : [];
            const hit = names.find((name: string) => String(name).toLowerCase() === subject.toLowerCase());
            if (hit) {
                setSubject(hit);
                setPackageId(String(pkg.id));
                break;
            }
        }
    }, [subject, packageId, packages]);

    useEffect(() => {
        if (!packageId) {
            setQuote(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await userService.quotePersonalClass({
                    packageId: Number(packageId),
                    subject,
                    classesPerWeek,
                    weeks,
                    ...(childId ? { childId: Number(childId) } : {}),
                });
                if (!cancelled) setQuote(response?.status ? response.data : null);
            } catch {
                if (!cancelled) setQuote(null);
            }
        })();
        return () => { cancelled = true; };
    }, [packageId, subject, classesPerWeek, weeks, childId, userService]);

    const selectedPackage = packages.find((item) => String(item.id) === String(packageId));
    const days = Array.from(new Set(slots.map((slot) => slot.dayText)));
    const child = children.find((item) => String(item.id) === String(childId));
    const chosenSlots = selections.map((selected) =>
        slots.find((slot) => selected && Number(slot.watDay) === Number(selected.watDay) && Number(slot.watHour) === Number(selected.watHour))
    );

    const setSelection = (index: number, slot: Slot | null) => {
        setSelections((current) => current.map((item, i) => (i === index ? (slot ? { watDay: slot.watDay, watHour: slot.watHour } : null) : item)));
    };

    const openReview = () => {
        if (!packageId) {
            toast.error('No private lesson is open for registration yet');
            return;
        }
        if (!childId || !grade.trim() || !subject) {
            toast.error('Choose a child, current grade/class, and subject');
            return;
        }
        if (selections.some((slot) => !slot)) {
            toast.error(`Choose ${classesPerWeek} class times`);
            return;
        }
        const unique = new Set(selections.map((slot) => `${slot?.watDay}-${slot?.watHour}`));
        if (unique.size !== selections.length) {
            toast.error('Each class needs a different time');
            return;
        }
        setReviewing(true);
    };

    const enrollmentPayload = (saveOnly: boolean) => ({
        packageId: Number(packageId),
        childId: Number(childId),
        subject,
        grade: grade.trim(),
        classesPerWeek,
        weeks,
        billingChoice: 'one_time',
        slots: selections
            .filter((slot): slot is { watDay: number; watHour: number } => Boolean(slot))
            .map((slot) => ({ watDay: slot.watDay, watHour: slot.watHour })),
        ...(saveOnly ? { addToCart: true } : {}),
    });

    const saveAndClose = async () => {
        if (closeLock.current) return;
        if (!reviewing || persisted.current) {
            onClose();
            return;
        }
        closeLock.current = true;
        setBusy('save');
        try {
            const response = await userService.checkoutPersonalClass(enrollmentPayload(true));
            if (!response?.status || !response.data?.enrollmentId) {
                toast.error(response?.message || 'Unable to save this lesson');
                closeLock.current = false;
                return;
            }
            persisted.current = true;
            toast.success('Registration saved. You can pay later from the lesson card.');
            onPaid?.();
            onClose();
        } catch (err: any) {
            closeLock.current = false;
            toast.error(err?.message || 'Unable to save this lesson');
        } finally {
            setBusy(null);
        }
    };

    const saveAndCloseRef = useRef(saveAndClose);
    saveAndCloseRef.current = saveAndClose;
    useImperativeHandle(ref, () => ({
        requestClose: () => { void saveAndCloseRef.current(); },
    }), []);

    const pay = async () => {
        if (closeLock.current) return;
        closeLock.current = true;
        setBusy('checkout');
        try {
            const response = await userService.checkoutPersonalClass(enrollmentPayload(false));
            if (!response?.status || !response.data?.provider) {
                toast.error(response?.message || 'Unable to open checkout');
                closeLock.current = false;
                return;
            }
            persisted.current = true;
            onPaid?.();
            onClose();
            startPersonalCheckout(response.data);
        } catch (err: any) {
            closeLock.current = false;
            toast.error(err?.message || 'Unable to start payment');
        } finally {
            setBusy(null);
        }
    };

    const section = 'rounded-[16px] border border-[#E8E0EC] bg-[#FBF9FC] p-4';

    return (
        <div className={modalBody}>
            <button type="button" aria-label="Close" className={modalCloseBtn} disabled={Boolean(busy)} onClick={() => { void saveAndClose(); }}>
                <X size={18} />
            </button>
            <div className="mb-5 pr-8">
                <div className="mb-3 flex items-center gap-2 text-[12px] font-semibold">
                    <span className={`rounded-full px-2.5 py-1 ${reviewing ? 'bg-[#F3E8F2] text-[#8b9aab]' : 'bg-ryd-primary text-white'}`}>1. Details</span>
                    <span className="h-px w-6 bg-[#E8E0EC]" />
                    <span className={`rounded-full px-2.5 py-1 ${reviewing ? 'bg-ryd-primary text-white' : 'bg-[#F3E8F2] text-[#8b9aab]'}`}>2. Payment</span>
                </div>
                <h2 className={modalTitle}>{reviewing ? 'Confirm this lesson' : 'Register for a private lesson'}</h2>
                <p className={modalSubtitle}>
                    {reviewing
                        ? 'Check the child, subject, and class times, then continue to checkout.'
                        : 'Choose the learner, subject, and a time for each class.'}
                </p>
            </div>

            {loading ? <p className="text-[14px] text-[#8b9aab]">Loading…</p> : null}

            {!loading && packages.length === 0 ? (
                <p className="text-[14px] text-[#8b9aab]">No private lesson is open for registration yet.</p>
            ) : null}

            {!loading && packages.length > 0 && !reviewing ? (
                <div className="space-y-4">
                    <section className={section}>
                        <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-ryd-primary">Learner</p>
                        <div className="space-y-3">
                            <div>
                                <label className={modalLabel}>Child</label>
                                {children.length === 0 ? (
                                    <button type="button" className={modalSecondaryBtn} onClick={openAddChildOnly}>
                                        Add a child
                                    </button>
                                ) : (
                                    <select className={modalField} value={childId} onChange={(e) => setChildId(e.target.value)}>
                                        <option value="">Select a child</option>
                                        {children.map((item) => (
                                            <option key={item.id} value={item.id}>
                                                {item.firstName} {item.lastName} · age {item.age}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div>
                                <label className={modalLabel}>Current Grade/Class</label>
                                <input className={modalField} value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="e.g. Grade 4" />
                            </div>
                        </div>
                    </section>

                    <section className={section}>
                        <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-ryd-primary">Lesson</p>
                        <div className="space-y-3">
                            <div ref={subjectMenuRef}>
                                <label className={modalLabel}>Subject</label>
                                <button
                                    type="button"
                                    className={`${modalField} flex items-center justify-between gap-2 text-left`}
                                    onClick={() => setSubjectOpen((open) => !open)}
                                >
                                    <span className={subject ? 'text-[#334155]' : 'text-[#94a3b8]'}>
                                        {subject || 'Select a subject'}
                                    </span>
                                    <ChevronDown size={16} className="shrink-0 text-[#94a3b8]" />
                                </button>
                                {subjectOpen ? (
                                    <div className="mt-1 overflow-hidden rounded-[12px] border border-[#E8E0EC] bg-white shadow-lg">
                                        <div className="border-b border-[#E8E0EC] p-2">
                                            <input
                                                autoFocus
                                                className={modalField}
                                                value={subjectQuery}
                                                onChange={(e) => setSubjectQuery(e.target.value)}
                                                placeholder="Search subject"
                                            />
                                        </div>
                                        <ul className="max-h-48 overflow-y-auto py-1">
                                    {offeredSubjects.length === 0 ? (
                                        <li className="px-4 py-2 text-[13px] text-[#8b9aab]">No subjects have been added to this lesson yet.</li>
                                    ) : filteredSubjects.length === 0 ? (
                                        <li className="px-4 py-2 text-[13px] text-[#8b9aab]">No subjects match</li>
                                    ) : filteredSubjects.map((item) => (
                                        <li key={`${item.packageId}-${item.name}`}>
                                            <button
                                                type="button"
                                                className={`w-full px-4 py-2 text-left text-[14px] hover:bg-[#F8F5FA] ${item.name === subject ? 'bg-[#F8F5FA] font-semibold text-ryd-primary' : 'text-[#334155]'}`}
                                                onClick={() => chooseSubject(item)}
                                            >
                                                {item.name}
                                            </button>
                                        </li>
                                    ))}
                                        </ul>
                                    </div>
                                ) : null}
                            </div>
                            <div>
                                <label className={modalLabel}>Classes per week</label>
                                <select
                                    className={modalField}
                                    value={classesPerWeek}
                                    onChange={(e) => {
                                        const count = Number(e.target.value);
                                        setClassesPerWeek(count);
                                        setSelections((current) => {
                                            const next = current.slice(0, count);
                                            while (next.length < count) next.push(null);
                                            return next;
                                        });
                                    }}
                                >
                                    <option value={1}>1</option>
                                    <option value={2}>2</option>
                                    <option value={3}>3</option>
                                    <option value={4}>4</option>
                                </select>
                            </div>
                        </div>
                    </section>

                    <section className={section}>
                        <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-ryd-primary">Class times</p>
                        <div className="space-y-3">
                            {selections.map((selected, index) => {
                                const chosen = slots.find((slot) => selected && Number(slot.watDay) === Number(selected.watDay) && Number(slot.watHour) === Number(selected.watHour));
                                const times = slots.filter((slot) => slot.dayText === chosen?.dayText);
                                return (
                                    <div key={index} className="grid gap-3 rounded-[12px] bg-white p-3 sm:grid-cols-2">
                                        <div>
                                            <label className={modalLabel}>Class {index + 1} day</label>
                                            <select
                                                className={modalField}
                                                value={chosen?.dayText || ''}
                                                onChange={(e) => {
                                                    const next = slots.find((slot) => slot.dayText === e.target.value);
                                                    setSelection(index, next || null);
                                                }}
                                            >
                                                <option value="">Select day</option>
                                                {days.map((day) => <option key={day} value={day}>{day}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={modalLabel}>Class {index + 1} time</label>
                                            <select
                                                className={modalField}
                                                value={chosen ? `${chosen.watDay}-${chosen.watHour}` : ''}
                                                onChange={(e) => {
                                                    const next = slots.find((slot) => `${slot.watDay}-${slot.watHour}` === e.target.value);
                                                    setSelection(index, next || null);
                                                }}
                                            >
                                                <option value="">Select time</option>
                                                {(chosen ? times : []).map((slot) => (
                                                    <option key={`${slot.watDay}-${slot.watHour}`} value={`${slot.watDay}-${slot.watHour}`}>
                                                        {slot.timeText}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                    <button type="button" className={modalPrimaryBtn} onClick={openReview}>
                        Continue to payment
                    </button>
                </div>
            ) : null}

            {!loading && reviewing ? (
                <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                        {[
                            ['Child', child ? `${child.firstName} ${child.lastName}` : '—'],
                            ['Grade', grade.trim() || '—'],
                            ['Subject', subject || selectedPackage?.title || '—'],
                            ['Classes per week', String(classesPerWeek)],
                        ].map(([label, value]) => (
                            <div key={label} className="rounded-[14px] border border-[#E8E0EC] px-4 py-3">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">{label}</p>
                                <p className="mt-1 text-[14px] font-semibold text-[#334155]">{value}</p>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-[14px] border border-[#E8E0EC] px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">Class times</p>
                        <ul className="mt-2 space-y-1 text-[14px] text-[#334155]">
                            {chosenSlots.map((slot, index) => (
                                <li key={index}>{slot ? `${slot.dayText} · ${slot.timeText}` : '—'}</li>
                            ))}
                        </ul>
                    </div>
                    {quote ? (
                        <div className={modalNotice}>
                            {quote.currency} {Number(quote.amount).toLocaleString()} for {quote.sessions} classes this month
                            ({classesPerWeek} per week).
                            The month starts the next Monday after registration. The invoice for the next month is sent at the start of the last week.
                        </div>
                    ) : (
                        <p className="text-[14px] text-[#8b9aab]">Calculating the amount…</p>
                    )}
                    <button type="button" className={modalPrimaryBtn} disabled={Boolean(busy) || !quote} onClick={() => { void pay(); }}>
                        {busy === 'checkout' ? 'Opening checkout…' : 'Checkout'}
                    </button>
                    <button type="button" className={modalSecondaryBtn} disabled={Boolean(busy)} onClick={() => setReviewing(false)}>
                        Back
                    </button>
                </div>
            ) : null}
        </div>
    );
});

export default PersonalClassModal;
